import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useComparableField } from '../../../context/useComparableField'
import {
  type FPlusExpressionValue,
  isNotRelevantField,
  type SubTemplateFieldValue,
} from '../../../types/templateModel'
import { isTemplateDependencyValidationError } from '../../../uiModels/types'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { stringifyComparableValue } from '../../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../../diffIndicators/ValueSetupDiffIndicators'
import { TemplateDocumentationEntry } from '../../documentationEntry/TemplateDocumentationEntry'
import { createLiteralTypeInformation } from '../../fplusExpression/expressionType'
import { TemplateFieldExpression } from '../../templateFieldExpression/TemplateFieldExpression'
import { updateVariant } from './helpers'
import { useTemplateOperationKeyFromPath } from './useTemplateOperationKeyFromPath'

type Props = {
  variant: SubTemplateFieldValue
  property: 'defaultSelected' | 'selectable'
}

export const SubTemplateFieldValueExpressionEntry = ({
  variant,
  property,
}: Props) => {
  const translate = useTranslate()
  const { subTemplatedField } = variant
  const { errors } = useOperationsContext()
  const getVariantOperationKey = useTemplateOperationKeyFromPath()
  const variantOperationKey = getVariantOperationKey(
    variant.origin ?? variant.path,
  )

  const fieldErrors = useMemo(
    () =>
      errors.filter(
        ({ error }) =>
          isTemplateDependencyValidationError(error) &&
          error.operationKey === variantOperationKey &&
          error.path === `${variant.path}.${property}`,
      ),
    [errors, property, variant.path, variantOperationKey],
  )

  assertNotEmpty(subTemplatedField)

  const comparableField = useComparableField<SubTemplateFieldValue>(
    `${variant.path}.${property}`,
  )

  const fplusCode = property in variant ? variant[property] : undefined

  const handleCodeChanged = useCallback(
    (nextValue: FPlusExpressionValue) =>
      updateVariant({
        variant,
        property,
        value: nextValue,
      }),
    [variant, property],
  )

  const valueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<SubTemplateFieldValue>) => {
      if (!comparable) {
        return ''
      }

      const fplusCode =
        property in comparable ? comparable[property] : undefined

      return stringifyComparableValue(fplusCode?.expression)
    },
    [property],
  )

  const expressionPath =
    fplusCode?.path ??
    `${subTemplatedField.path}{${variant.subTemplateIdentifier}}.${property}`

  return (
    <TemplateDocumentationEntry
      title={
        property === 'defaultSelected'
          ? translate('templateViewer.fieldDetailsModal.field.defaultSelected')
          : translate('templateViewer.fieldDetailsModal.field.selectable')
      }
      isShowingErrors
      errors={fieldErrors}
      diffIndicator={
        <ValueSetupDiffIndicators
          comparableValues={comparableField}
          serialiseValueFn={valueFromComparable}
          compareFilter={property}
        />
      }
      value={
        <TemplateFieldExpression
          key={expressionPath}
          expression={fplusCode?.expression}
          path={expressionPath}
          fallbackCode=""
          onChange={
            variant.subTemplatedField &&
            isNotRelevantField(variant.subTemplatedField)
              ? undefined
              : handleCodeChanged
          }
          isExpanded
          typeInformation={createLiteralTypeInformation({
            literalType: 'boolean',
            source: fplusCode?.expression?.fplusSource,
          })}
          fromOrigin={fplusCode?.origin}
        />
      }
      path={variant[property]?.path ?? `${variant.path}.${property}`}
      origin={
        variant[property]?.origin ??
        variant[property]?.path ??
        `${variant.path}.${property}`
      }
    />
  )
}
